import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { track } from '../analytics/events.js';
import { scoreToColor } from '../map/areaData.js';
import { scoreScaleWords } from '../map/scoreWords.js';
import './faq.css';

// Frequently asked questions at /faq. Answers explain what the site does in plain words, without the
// details of how scores or listings are produced. Keep answers short (one or two sentences).

const SCALE_STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];

const SECTIONS = {
    sv: [
        {
            id: 'about',
            title: 'Om hempejl',
            items: [
                { id: 'what', q: 'Vad är hempejl?', a: 'Bostäder till salu och statistik om områdena på samma karta, så att du kan välja bostad och område samtidigt.' },
                { id: 'cost', q: 'Kostar det något?', a: 'Nej, det är gratis att söka och jämföra.' },
            ],
        },
        {
            id: 'score',
            title: 'Betyget',
            items: [
                { id: 'score-meaning', q: 'Vad betyder betyget?', a: 'Betyget visar hur ett område står sig mot områdena runt omkring, från 1 till 6. Runt 3,5 är som närområdet och högre betyder att området sticker ut. I ord: Exklusivt, Eftertraktat, Attraktivt, Stabilt, Enklare och Ansträngt.', scale: true },
                { id: 'score-why-nearby', q: 'Varför jämförs områden med närområdet?', a: 'Priser och inkomster skiljer sig mycket mellan stad och landsbygd. Jämförelsen med grannarna visar vad som utmärker ett område där du faktiskt letar.' },
                { id: 'score-inputs', q: 'Vad vägs in i betyget?', a: 'Inkomst, sysselsättning, utbildning, andelen ägda bostäder och kvadratmeterpriser.' },
                { id: 'score-low', q: 'Är ett område med lågt betyg dåligt?', a: 'Nej. Betyget bygger bara på statistik. Trivsel, natur och kommunikationer syns inte i siffrorna, och ett lägre betyg betyder ofta lägre priser.' },
            ],
        },
        {
            id: 'map',
            title: 'Kartan och bostäderna',
            items: [
                { id: 'area', q: 'Vad är ett område?', a: 'Sverige är indelat i drygt 3 000 statistikområden, ofta en stadsdel eller en del av en ort. Varje område får sin egen färg på kartan.' },
                { id: 'no-listings', q: 'Varför syns inga bostäder på kartan?', a: 'Sök på en ort eller adress först, så visas bostäderna där.' },
                { id: 'views', q: 'Vad är skillnaden mellan kartvy och listvy?', a: 'I kartvyn utforskar du hela Sverige fritt. I listvyn väljer du områden och bläddrar bland bostäderna i en lista.' },
                { id: 'filters', q: 'Hur fungerar filtren?', a: 'Du kan filtrera bostäder på typ, pris, rum och storlek, och områden på betyg. Områden utanför dina val blir grå.' },
                { id: 'hide-area', q: 'Hur väljer jag områden på kartan?', a: 'Klicka på ett område för att lägga till det i sökningen. Klicka igen för att ta bort det.' },
            ],
        },
        {
            id: 'listings',
            title: 'Annonserna',
            items: [
                { id: 'listings-source', q: 'Var kommer bostäderna ifrån?', a: 'Vi samlar annonser som redan är publicerade öppet på nätet, ungefär som en sökmotor. Hela annonsen finns hos mäklaren, och vi länkar dit.' },
                { id: 'listings-why-mine', q: 'Varför finns min bostad här?', a: 'Den är till salu i en öppen annons. Vi visar bara uppgifter som redan är publicerade där, aldrig namn på säljare eller köpare.' },
                { id: 'listings-remove', q: 'Kan jag få min bostad borttagen?', a: 'Inte så länge den är till salu öppet, eftersom alla bostäder till salu ska gå att hitta. När annonsen tas bort hos mäklaren försvinner den här också, oftast inom ett dygn.' },
                { id: 'listings-sold', q: 'Visar ni sålda bostäder?', a: 'Nej. Slutpriser används bara sammanräknade, som kvadratmeterpriset för ett helt område.' },
                { id: 'listings-wrong', q: 'Något stämmer inte i en annons. Vad gör jag?', a: 'Uppgifterna kommer från mäklarens annons. Rätta dem där, så uppdateras de här också.' },
            ],
        },
        {
            id: 'data',
            title: 'Data och kostnader',
            items: [
                { id: 'stats-source', q: 'Var kommer statistiken ifrån?', a: 'Från officiell statistik, främst SCB. Den uppdateras när ny statistik publiceras.' },
                { id: 'listings-fresh', q: 'Hur aktuella är bostäderna?', a: 'Bostäderna uppdateras löpande. Hela annonsen och visningar hittar du hos mäklaren.' },
                { id: 'monthly-cost', q: 'Hur räknas boendekostnaden ut?', a: 'Det är en uppskattning av ränta, amortering och avgift utifrån kontantinsatsen och räntan, som du kan ändra själv. Den ersätter inte ett lånelöfte.' },
            ],
        },
    ],
    en: [
        {
            id: 'about',
            title: 'About hempejl',
            items: [
                { id: 'what', q: 'What is hempejl?', a: 'Homes for sale and statistics about the areas on one map, so you can choose a home and an area at the same time.' },
                { id: 'cost', q: 'Does it cost anything?', a: 'No, searching and comparing is free.' },
            ],
        },
        {
            id: 'score',
            title: 'The score',
            items: [
                { id: 'score-meaning', q: 'What does the score mean?', a: 'The score shows how an area compares with the areas around it, from 1 to 6. Around 3.5 is like nearby areas, and higher means the area stands out. In words: Exclusive, Sought-after, Attractive, Steady, Modest and Strained.', scale: true },
                { id: 'score-why-nearby', q: 'Why are areas compared with nearby areas?', a: 'Prices and incomes differ a lot between cities and the countryside. Comparing with the neighbours shows what makes an area stand out where you are actually looking.' },
                { id: 'score-inputs', q: 'What goes into the score?', a: 'Income, employment, education, the share of owned homes and prices per m².' },
                { id: 'score-low', q: 'Is an area with a low score bad?', a: 'No. The score is based on statistics only. Atmosphere, nature and transport do not show up in the numbers, and a lower score often means lower prices.' },
            ],
        },
        {
            id: 'map',
            title: 'The map and the listings',
            items: [
                { id: 'area', q: 'What is an area?', a: 'Sweden is divided into just over 3,000 statistical areas, often a district or part of a town. Each area gets its own colour on the map.' },
                { id: 'no-listings', q: 'Why are there no listings on the map?', a: 'Search for a town or address first, and the listings there will appear.' },
                { id: 'views', q: 'What is the difference between kartvy and listvy?', a: 'In kartvy you explore all of Sweden freely. In listvy you pick areas and browse their listings in a list.' },
                { id: 'filters', q: 'How do the filters work?', a: 'You can filter listings by type, price, rooms and size, and areas by score. Areas outside your choices turn grey.' },
                { id: 'hide-area', q: 'How do I pick areas on the map?', a: 'Click an area to add it to the search. Click again to remove it.' },
            ],
        },
        {
            id: 'listings',
            title: 'The listings',
            items: [
                { id: 'listings-source', q: 'Where do the listings come from?', a: 'We collect listings that are already published openly online, much like a search engine. The full listing is with the realtor, and we link to it.' },
                { id: 'listings-why-mine', q: 'Why is my home here?', a: 'It is for sale in a public listing. We only show details already published there, never the names of sellers or buyers.' },
                { id: 'listings-remove', q: 'Can I have my home removed?', a: 'Not while it is openly for sale, since every home for sale should be possible to find. When the realtor takes the listing down, it disappears here too, usually within a day.' },
                { id: 'listings-sold', q: 'Do you show sold homes?', a: 'No. Sold prices are only used combined, as the price per m² for a whole area.' },
                { id: 'listings-wrong', q: 'Something in a listing is wrong. What do I do?', a: 'The details come from the realtor\'s listing. Correct them there, and they will update here too.' },
            ],
        },
        {
            id: 'data',
            title: 'Data and costs',
            items: [
                { id: 'stats-source', q: 'Where do the statistics come from?', a: 'From official statistics, mainly Statistics Sweden (SCB). They are updated when new figures are published.' },
                { id: 'listings-fresh', q: 'How up to date are the listings?', a: 'Listings are updated continuously. The full listing and viewings are with the realtor.' },
                { id: 'monthly-cost', q: 'How is the monthly cost calculated?', a: 'It is an estimate of interest, amortization and fee based on the down payment and rate, which you can change yourself. It does not replace a mortgage offer.' },
            ],
        },
    ],
};

const FaqPage = ({ languageState }) => {
    const swedish = languageState?.swedish !== false;
    const sections = SECTIONS[swedish ? 'sv' : 'en'];

    useEffect(() => {
        track('faq.view');
    }, []);

    const onToggle = (id) => (event) => {
        if (event.currentTarget.open) track('faq.open', { question: id });
    };

    return (
        <div className="faq-page">
            <div className="faq-inner">
                <Link to="/" className="faq-back">
                    <ArrowBack fontSize="inherit" />
                    {swedish ? 'Tillbaka till kartan' : 'Back to the map'}
                </Link>
                <header className="faq-header">
                    <h1>{swedish ? 'Vanliga frågor' : 'Frequently asked questions'}</h1>
                    <p>{swedish ? 'Hur hempejl fungerar, kort förklarat.' : 'How hempejl works, in short.'}</p>
                </header>

                {sections.map((section) => (
                    <section key={section.id} className="faq-section" aria-labelledby={`faq-${section.id}`}>
                        <h2 id={`faq-${section.id}`}>{section.title}</h2>
                        <div className="faq-list">
                            {section.items.map((item) => (
                                <details key={item.id} className="faq-item" onToggle={onToggle(item.id)}>
                                    <summary>
                                        <span>{item.q}</span>
                                        <span className="faq-chevron" aria-hidden="true" />
                                    </summary>
                                    <div className="faq-answer">
                                        <p>{item.a}</p>
                                        {item.scale ? (
                                            <div className="faq-scale" aria-hidden="true">
                                                <div className="faq-scale-bar">
                                                    {SCALE_STEPS.map((step) => <i key={step} style={{ background: scoreToColor[step] }} />)}
                                                </div>
                                                <div className="faq-scale-ends">
                                                    {scoreScaleWords(swedish).map((label) => <span key={label}>{label}</span>)}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </details>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
};

export default FaqPage;
